import { DataResult, DataStateChangeEvent, GridDataResult, PageChangeEvent, State, pageSkip, pageTake, process } from 'src/app/Shared/prime-ng/grid-data';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';
import { Component, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { PopupRequest, AuthenticationResult } from '@azure/msal-browser';
import { AuthService } from 'src/app/Shared/Services/authentication.service';
import { AuthResponse, isAuthError, isAuthSuccess, isMfaEnabled, isMfaRequired, isMfaSetupRequired, MfaSetupData } from 'src/app/Shared/Models/Auth.model';


type LoginStep = 'choice' | 'credentials' | 'mfaVerify' | 'mfaSetup' | 'backupCodes';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [PrimeNgModule, CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent {
  // State
  step: LoginStep = 'choice';
  loading = false;
  error = '';

  // Form
  username = '';
  password = '';
  mfaCode = '';

  // MFA
  tempToken = '';
  mfaSetup: MfaSetupData | null = null;
  backupCodes: string[] = [];

  constructor(
    private authService: AuthService,
    private router: Router,
    private msalService: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) { }

  // ===== NAVIGATION =====

  selectMfaLogin(): void {
    this.step = 'credentials';
  }

  goBack(): void {
    this.step = 'choice';
    this.resetForm();
  }

  // ===== AZURE LOGIN =====

  loginWithAzure(): void {
    this.loading = true;
    this.error = '';

    const request = this.msalGuardConfig.authRequest
      ? { ...this.msalGuardConfig.authRequest } as PopupRequest
      : undefined;

    this.msalService.loginPopup(request).subscribe({
      next: (result: AuthenticationResult) => {
        this.msalService.instance.setActiveAccount(result.account);
        this.handleAzureSuccess(result);
      },
      error: (err) => {
        console.error('Azure Login Error:', err);
        this.handleError('Azure Login fehlgeschlagen');
      }
    });
  }

  private handleAzureSuccess(result: AuthenticationResult): void {
    this.authService.azureLogin(result).subscribe({
      next: (response) => {
        if (isAuthSuccess(response)) {
          this.router.navigateByUrl(this.authService.getDefaultRoute());
        } else if (isAuthError(response)) {
          this.handleError(response.error);
          this.msalService.logoutPopup({ mainWindowRedirectUri: '/login' });
        }
        this.loading = false;
      },
      error: (err) => {
        this.handleError(err.error?.error || 'Azure Login fehlgeschlagen');
        this.msalService.logoutPopup({ mainWindowRedirectUri: '/login' });
      }
    });
  }

  // ===== CREDENTIALS LOGIN =====

  login(): void {
    this.loading = true;
    this.error = '';

    this.authService.login(this.username, this.password).subscribe({
      next: (response) => this.handleAuthResponse(response),
      error: (err) => this.handleError(err.error?.error || 'Ungültige Anmeldedaten')
    });
  }

  // ===== MFA VERIFY =====

  verifyMfa(): void {
    if (this.mfaCode.length !== 6) return;

    this.loading = true;
    this.error = '';

    this.authService.verifyMfa(this.tempToken, this.mfaCode).subscribe({
      next: (response) => this.handleAuthResponse(response),
      error: (err) => {
        this.handleError(err.error?.error || 'Invalid Code - Try to reload the page');
        this.mfaCode = '';
      }
    });
  }

  // ===== MFA ENABLE =====

  enableMfa(): void {
    if (this.mfaCode.length !== 6) {
      this.error = 'Bitte 6-stelligen Code eingeben';
      return;
    }

    this.loading = true;
    this.error = '';

    this.authService.enableMfa(this.tempToken, this.mfaCode).subscribe({
      next: (response) => {
        if (isMfaEnabled(response)) {
          this.backupCodes = response.backupCodes;
          this.step = 'backupCodes';
          this.loading = false;
        } else {
          this.handleAuthResponse(response);
        }
      },
      error: (err) => {
        this.handleError(err.error?.error || 'Ungültiger Code');
        this.mfaCode = '';
      }
    });
  }

  // ===== RESPONSE HANDLER =====

  private handleAuthResponse(response: AuthResponse): void {
    this.loading = false;

    if (isAuthSuccess(response)) {
      this.router.navigateByUrl(this.authService.getDefaultRoute());
      return;
    }

    if (isMfaRequired(response)) {
      this.tempToken = response.tempToken;
      this.step = 'mfaVerify';
      return;
    }

    if (isMfaSetupRequired(response)) {
      this.tempToken = response.tempToken;
      this.mfaSetup = response.mfaSetup;
      this.step = 'mfaSetup';
      return;
    }

    if (isAuthError(response)) {
      this.error = response.error;
    }
  }

  // ===== HELPERS =====

  private handleError(message: string): void {
    this.error = message;
    this.loading = false;
  }

  private resetForm(): void {
    this.username = '';
    this.password = '';
    this.mfaCode = '';
    this.tempToken = '';
    this.mfaSetup = null;
    this.backupCodes = [];
    this.error = '';
  }

  copySecret(): void {
    if (this.mfaSetup) {
      navigator.clipboard.writeText(this.mfaSetup.secret);
    }
  }

  downloadBackupCodes(): void {
    const text = this.backupCodes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'labtech-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  continueToApp(): void {
    this.router.navigateByUrl(this.authService.getDefaultRoute());
  }
}
